/**
 * @coeldery/empty-state — 通用空狀態（標題 ＋ 一句引導 ＋ 可選行動）
 *
 * 為何要模組（唔係 app-local）：`ui-spec.md` §六 要求**每個畫面都要有空狀態**，而且
 * 「引導而非教導」—— 呢個係跨畫面共用嘅模式（第二個 app 都會用得到）→ 依 R3 做成模組。
 *
 * ⚠️ 設計（順應 `rules.md` A1／A5：唔急於建議、先陪伴後產出）：
 *   · 只出「一句」引導句；唔准列步驟／教學／KPI 式清單
 *   · 文字一律由 `textKey` 傳 i18n key（模組**唔准**寫死文字）
 *   · 顏色只用 CSS var（`var(--color-*)`）
 */
import { useTranslation } from 'react-i18next';

export interface EmptyStateProps {
  /** i18n key：標題 */
  titleKey: string;
  /** i18n key：一句引導（**唔准**長篇） */
  bodyKey: string;
  /** 可選：主要行動（例「開始傾偈」）；唔傳就唔顯示 */
  action?: { labelKey: string; onClick: () => void };
  /** 可選：裝飾用 icon（ReactNode；唔傳就唔顯示） */
  icon?: React.ReactNode;
}

export default function EmptyState({ titleKey, bodyKey, action, icon }: EmptyStateProps) {
  const { t } = useTranslation();
  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        padding: '40px 20px',
        textAlign: 'center',
        color: 'var(--color-text-secondary)',
      }}
      aria-live="polite"
    >
      {icon ? <div aria-hidden="true">{icon}</div> : null}
      <h2 style={{ margin: 0, fontSize: '17px', color: 'var(--color-text)' }}>{t(titleKey)}</h2>
      <p style={{ margin: 0, fontSize: 'var(--font-size-button, 17px)', maxWidth: '28em', lineHeight: 1.6 }}>{t(bodyKey)}</p>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            marginTop: '8px',
            minHeight: '44px',
            padding: '0 20px',
            border: 'none',
            borderRadius: '22px',
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-on-primary)',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-body, 18px)',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t(action.labelKey)}
        </button>
      ) : null}
    </section>
  );
}
